import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, LoaderCircle } from "lucide-react";
import apiClient from "../../business/network/ApiClient";

/**
 * Component 3 — profile settings modal, faithful to the "AskMila Settings Popup" design page:
 * two-column rows (label + caption left, input right) with thin dividers, a locked Full name
 * sourced from the organization account, and an "About me" section with its own heading,
 * explanation and footnote. Distance units and Currency are separate rows (product decision).
 * Reads live via GET /Onboarding/Profile (memory has priority, OU regional settings fill the
 * gaps), saves via PUT /Onboarding/Profile (SetMemory).
 * Enumerable fields (role, dialect, units, volumeUnits, currency) are selects fed by
 * GET /Onboarding/ProfileOptions — free text there could push values outside the vocabulary
 * the extractor and enum validation understand; the select falls back to a text input only
 * when the options endpoint is unavailable.
 * "What matters most to you" (interests) is deliberately NOT a row: it left the Mila AI
 * contract on 2026-08-10 — it is a conversation input that About me carries, and About me is
 * editable right here.
 */
const FIELDS = [
  { key: "preferredName", label: "What should Mila call you?" },
  { key: "role", label: "Your role", select: true },
  { key: "country", label: "Country", select: true },
  {
    key: "dialect",
    label: "Language",
    caption: "The language and dialect Mila replies in",
    select: true,
  },
  { key: "units", label: "Distance units", select: true },
  { key: "volumeUnits", label: "Volume units", select: true },
  { key: "currency", label: "Currency", select: true },
];

/**
 * Fully styled dropdown — a native <select> popup takes the browser/OS theme (it flashed
 * black on open) and can't be styled, so the options panel is our own: input-look trigger,
 * white rounded panel, hover rows, check mark on the selected entry, which is scrolled
 * into view on open (matters for the ~95-entry country list).
 */
const SelectField = ({ value, options, extraOption, onChange }) => {
  const [open, setOpen] = useState(false);
  const selectedRef = useRef(null);
  const allOptions = extraOption ? [extraOption, ...options] : options;

  useEffect(() => {
    if (open && selectedRef.current) {
      selectedRef.current.scrollIntoView({ block: "nearest" });
    }
  }, [open]);

  return (
    <div
      className="relative flex-1 min-w-0"
      onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 bg-white border border-[#E9EAF0] rounded-xl px-3 py-2 text-[13px] text-left focus:outline-none focus:border-blue-200"
      >
        <span className={`truncate ${value ? "text-dark-blue-100" : "text-dimgray-200"}`}>
          {value || "Select…"}
        </span>
        <ChevronDown
          className={`w-3.5 h-3.5 shrink-0 text-dimgray-200 transition-transform duration-150 ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} />
          <div className="absolute left-0 right-0 top-full mt-1 z-20 bg-white border border-[#E9EAF0] rounded-xl shadow-lg max-h-52 overflow-y-auto py-1">
            {allOptions.map((o) => {
              const selected = o === value;
              return (
                <button
                  key={o}
                  type="button"
                  ref={selected ? selectedRef : null}
                  onClick={() => {
                    onChange(o);
                    setOpen(false);
                  }}
                  className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-[13px] text-left text-dark-blue-100 hover:bg-[#F5F6F8] ${
                    selected ? "font-semibold bg-[#F5F6F8]" : ""
                  }`}
                >
                  <span className="truncate">{o}</span>
                  {selected && <Check className="w-3.5 h-3.5 shrink-0" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

// Stored values are canonical ("kilometers"), but memory may hold a different casing —
// snap them onto the option's exact form so the select shows them as selected instead
// of listing a near-duplicate.
const snapToOptions = (profile, options) => {
  const snapped = { ...profile };
  for (const [key, opts] of Object.entries(options)) {
    const current = snapped[key];
    if (!current || !Array.isArray(opts)) continue;
    const match = opts.find(
      (o) => o.toLowerCase() === String(current).trim().toLowerCase()
    );
    if (match) snapped[key] = match;
  }
  return snapped;
};

const UserSettingsModal = ({ userId, ouId, fullName, onClose }) => {
  const [values, setValues] = useState({});
  const [initialValues, setInitialValues] = useState({});
  const [options, setOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      // Whatever happens below, the spinner has to come down: an unhandled rejection here used
      // to leave the modal spinning forever with no way out.
      try {
        const [profile, opts] = await Promise.all([
          apiClient.getOnboardingProfile(
            userId,
            ouId,
            navigator.language || navigator.userLanguage || "en-GB"
          ),
          apiClient.getOnboardingProfileOptions(),
        ]);
        const snapped = snapToOptions(profile || {}, opts || {});
        setOptions(opts || {});
        setValues(snapped);
        setInitialValues(snapped);
        if (!profile) setError("Couldn't load your preferences. You can still edit them below.");
      } catch (e) {
        console.error("Failed to load the settings modal:", e);
        setError("Couldn't load your preferences. You can still edit them below.");
      } finally {
        setLoading(false);
      }
    };
    if (userId) load();
  }, [userId, ouId]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await apiClient.updateOnboardingProfile(userId, values);
      onClose();
    } catch (e) {
      setError("Couldn't save your preferences. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm flex items-center justify-center z-50 p-4 font-notosans">
      <div className="bg-white rounded-3xl shadow-2xl max-w-xl w-full p-7 max-h-[94vh] overflow-y-auto scrollbar-hide">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-dark-blue-100 font-urbanist text-[22px] font-extrabold">
            What Mila knows about you
          </h2>
          <button
            onClick={onClose}
            className="text-dimgray-200 hover:text-dark-blue-100 text-xl leading-none"
          >
            &times;
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-10">
            <LoaderCircle className="w-6 h-6 animate-spin text-dark-blue-100" />
          </div>
        ) : (
          <div className="flex flex-col">
            {/* Full name — from the organization account, read-only (design) */}
            <div className="flex items-start gap-4 py-2.5 border-b border-[#F0F0F5]">
              <div className="w-[42%] shrink-0">
                <label className="block text-[13px] font-semibold text-dark-blue-100">
                  Full name
                </label>
                <span className="block text-[11px] leading-4 text-dimgray-200 mt-0.5">
                  From your organization account
                </span>
              </div>
              <div className="relative flex-1">
                <input
                  type="text"
                  disabled
                  value={fullName || ""}
                  className="w-full bg-[#F5F6F8] border border-[#E9EAF0] rounded-xl px-3 py-2 pr-9 text-[13px] text-dimgray-200 cursor-not-allowed"
                />
                <svg
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-dimgray-200"
                  width="13"
                  height="13"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                  <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                </svg>
              </div>
            </div>

            {FIELDS.map((f) => {
              const fieldOptions = Array.isArray(options[f.key]) ? options[f.key] : [];
              const asSelect = f.select && fieldOptions.length > 0;
              // A value captured as free text in chat (e.g. interests in the user's own
              // words) still has to display as the selected entry — carry it as an extra
              // option so switching to a canonical choice stays revertible until Save.
              const extraOption =
                asSelect &&
                initialValues[f.key] &&
                !fieldOptions.some(
                  (o) => o.toLowerCase() === initialValues[f.key].toLowerCase()
                )
                  ? initialValues[f.key]
                  : null;
              return (
                <div key={f.key} className="flex items-start gap-4 py-2.5 border-b border-[#F0F0F5]">
                  <div className="w-[42%] shrink-0">
                    <label className="block text-[13px] font-semibold text-dark-blue-100 pt-2">
                      {f.label}
                    </label>
                    {f.caption && (
                      <span className="block text-[11px] leading-4 text-dimgray-200 mt-0.5">
                        {f.caption}
                      </span>
                    )}
                  </div>
                  {asSelect ? (
                    <SelectField
                      value={values[f.key] || ""}
                      options={fieldOptions}
                      extraOption={extraOption}
                      onChange={(val) => setValues((v) => ({ ...v, [f.key]: val }))}
                    />
                  ) : (
                    <input
                      type="text"
                      className="flex-1 min-w-0 border border-[#E9EAF0] rounded-xl px-3 py-2 text-[13px] text-dark-blue-100 focus:outline-none focus:border-blue-200"
                      value={values[f.key] || ""}
                      onChange={(e) => setValues((v) => ({ ...v, [f.key]: e.target.value }))}
                    />
                  )}
                </div>
              );
            })}

            {/* About me — its own highlighted section (design) */}
            <div className="pt-4">
              <h3 className="text-dark-blue-100 font-urbanist text-[18px] font-extrabold mb-1">
                About me
              </h3>
              <p className="text-dimgray-200 text-[12px] leading-4 mb-2">
                What you shared with Mila during onboarding — you can also edit it
                here or ask Mila to save information here at any time.
              </p>
              <textarea
                rows={4}
                className="w-full border border-[#E9EAF0] rounded-xl px-3 py-2 text-[13px] leading-5 text-dark-blue-100 focus:outline-none focus:border-blue-200 resize-none"
                value={values.aboutMe || ""}
                onChange={(e) => setValues((v) => ({ ...v, aboutMe: e.target.value }))}
              />
              <p className="text-dimgray-200 text-[11px] leading-4 mt-1">
                Mila uses this to personalize your answers. You can edit or clear it
                at any time.
              </p>
            </div>

            {error && <p className="text-red-500 text-[12px] mt-3">{error}</p>}

            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={onClose}
                className="px-6 py-2.5 rounded-[14px] border border-[#E9EAF0] text-dark-blue-100 text-[13px] font-semibold hover:border-dimgray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-2.5 rounded-[14px] bg-dark-blue-100 hover:bg-dark-blue-hover text-white text-[13px] font-bold disabled:opacity-70"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserSettingsModal;
